/**
 *
 * SelectRecipients
 *
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Form } from 'react-bootstrap';
import { inputMixin } from 'components/tags/Input/index.jsx';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import Snoozing from 'components/user/Snoozing.jsx';
import SchoolDisplay from 'components/user/SchoolDisplay.jsx';
import { PARENT_ROLE, USERROLES } from 'containers/User/constants.js';
import messages from './messages.js';
import SelectUsers from '../SelectUsers/index.jsx';
import UserTags from '../SelectUsers/UserTags.jsx';

const Wrapper = styled.div.attrs({ role: 'button', tabIndex: 0 })`
  min-height: 34px;
  width: 100%;
  text-align: left;
  padding-left: 15px;
  ${inputMixin};
  color: ${(p) => p.theme.linkColor};
`;

const FormCheckDivLabel = styled(Form.Check)`
  width: 100%;
  .form-check-label {
    display: block
  }
`;

function showUser(user, attributes) {
  const children = user.roleType === PARENT_ROLE && user.connections.length > 0;
  const label = (
    <>
      <div className="d-flex">
        <div className="flex-grow-1"><UserAvatar user={user} /></div>
        <Snoozing user={user} />
        {user.school && <SchoolDisplay school={user.school} />}
      </div>
      {children && (
        <ul className="list-unstyled small text-muted mb-0 mt-1 lh-sm">
          {user.connections.map((c) => <li key={c.id}>{c.displayName}</li>)}
        </ul>
      )}
    </>
  );
  return (
    <div className="d-flex align-items-baseline justify-content-between">
      <FormCheckDivLabel type="checkbox" id={user.id} {...attributes} label={label} />
    </div>
  );
}

function SelectRecipients(props) {
  const { value = [], disabled, onPopup, alwaysOpen, onCancel, prompt = messages.prompt, roles = USERROLES, inboxId } = props;
  const [show, setShow] = useState(alwaysOpen || value.length === 0);
  const scrollTop = useRef(document.documentElement.scrollTop);
  useEffect(() => {
    if (onPopup) {
      onPopup(show);
    }
  }, [show]);

  function openModal() {
    scrollTop.current = document.documentElement.scrollTop;
    setShow(true);
  }

  function closeModal(submitted) {
    if (!alwaysOpen) {
      setShow(false);
    }
    if (onCancel) {
      onCancel(submitted);
    }
    window.setTimeout(() => {
      document.documentElement.scrollTop = scrollTop.current;
    }, 0);
  }

  function keyDown(ev) {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      openModal();
    }
  }

  if (disabled) {
    return <UserTags prompt={prompt} {...props} />;
  }
  const fetchOptions = {
    url: 'recipients',
    typeField: 'connectionRoleType',
  };
  return (
    <>
      {!alwaysOpen && (
        <Wrapper onClick={openModal} onKeyDown={keyDown}>
          <UserTags prompt={prompt} {...props} />
        </Wrapper>
      )}
      <SelectUsers
        show={show}
        fetchOptions={fetchOptions}
        inboxId={inboxId}
        prompt={prompt}
        onHide={closeModal}
        showUser={showUser}
        roles={roles}
        {...props}
      />
    </>
  );
}

SelectRecipients.propTypes = {
  alwaysOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
  value: PropTypes.array, // eslint-disable-line react/no-unused-prop-types
  onChange: PropTypes.func, // eslint-disable-line react/no-unused-prop-types
  onPopup: PropTypes.func,
  prompt: PropTypes.object,
  roles: PropTypes.array,
  inboxId: PropTypes.string,
};

SelectRecipients.getSchema = SelectUsers.getSchema;

export default SelectRecipients;
